'use client';

import { useTranslations } from 'next-intl';
import { ComponentProps, useMemo } from 'react';
import { FormProvider, useWatch } from 'react-hook-form';
import { DeliveryType } from '@/features/enums/DeliveryType';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { AddressInput } from '@/shared/model/Address';
import { LocalizedString } from '@/shared/model/Language';
import { FormAddressAutocompleteField } from '@/shared/ui/form/form-address-autocomplete-field';
import { Icon } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';
import type { PageComponentData } from '@/widgets/prefab-renderer';
import { useVenueSelectFilters } from '../../hooks/useVenueSelectFilters';
import { CompleteVenueWithGeoData } from '../../types/CompleteVenueWithGeoData';
import { SelectDeliveryBoxDeliveryOption } from './SelectDeliveryBoxDeliveryOption';
import { SelectDeliveryBoxVenueList } from './SelectDeliveryBoxVenueList';
import './styles.scss';

export type FormValues = {
    deliveryType: DeliveryType;
    address: AddressInput | Record<string, never>;
    selectedVenue: CompleteVenueWithGeoData | null;
    deliveryVenues: CompleteVenueWithGeoData[];
    pickupVenues: CompleteVenueWithGeoData[];
};

type Props = {
    /** Show delivery option */
    isDeliveryAvailable?: boolean;
    /** Show pickup option */
    isPickupAvailable?: boolean;
    /** Show icon in delivery options */
    isIconVisible?: boolean;
    /** Custom delivery title */
    deliveryTitle?: LocalizedString;
    /** Custom pickup title */
    pickupTitle?: LocalizedString;
    /** Custom delivery time */
    deliveryTime?: number;
    /** Custom pickup time */
    pickupTime?: number;
    /** Custom delivery option heading */
    deliveryHeading?: LocalizedString;
    /** Custom pickup heading */
    pickupHeading?: LocalizedString;
    /** Custom venue card prefab */
    venueCard?: PageComponentData;
    /** Delay before auto-redirect when only single venue is available */
    autoRedirectDelayMs?: number;
    'data-testid'?: string;
} & ComponentProps<typeof Layout>;

export const SelectDeliveryBox = ({
    isDeliveryAvailable = true,
    isPickupAvailable = true,
    isIconVisible = true,
    deliveryTitle,
    pickupTitle,
    deliveryTime,
    pickupTime,
    deliveryHeading,
    pickupHeading,
    venueCard,
    autoRedirectDelayMs = 1_000,
    ...props
}: Props) => {
    const t = useTranslations();
    const getLocalizedValue = useGetLocalizedValue();
    const {
        formMethods,
        handleChangeAddress,
        handleChangeDeliveryType,
        isDeliveryAvailable: isDeliveryPossible,
        isPickupAvailable: isPickupPossible,
    } = useVenueSelectFilters({
        autoRedirect: true,
        autoRedirectDelayMs,
    });

    const [deliveryType, address, selectedVenue] = useWatch({
        control: formMethods.control,
        name: ['deliveryType', 'address', 'selectedVenue', 'deliveryVenues', 'pickupVenues'],
    });

    const filteredVenues = useWatch({
        control: formMethods.control,
        name: ['deliveryType', 'deliveryVenues', 'pickupVenues'],
        compute: ([deliveryType, deliveryVenues, pickupVenues]) =>
            deliveryType === DeliveryType.DELIVERY ? deliveryVenues : pickupVenues,
    });

    const tiles = useMemo(() => {
        const tiles = [];
        if (isDeliveryPossible && isDeliveryAvailable) {
            tiles.push({
                type: DeliveryType.DELIVERY,
                title: deliveryTitle
                    ? getLocalizedValue(deliveryTitle)
                    : t('SELECT_DELIVERY_BOX.DELIVERY_TITLE'),
                icon: 'car',
                time: deliveryTime,
                testId: 'select-delivery-box-delivery-tile',
            });
        }
        if (isPickupPossible && isPickupAvailable) {
            tiles.push({
                type: DeliveryType.PICKUP,
                title: pickupTitle ? getLocalizedValue(pickupTitle) : t('SELECT_DELIVERY_BOX.PICKUP_TITLE'),
                icon: 'store',
                time: pickupTime,
                testId: 'select-delivery-box-pickup-tile',
            });
        }
        return tiles;
    }, [
        t,
        getLocalizedValue,
        isDeliveryAvailable,
        isPickupAvailable,
        deliveryTime,
        pickupTime,
        deliveryTitle,
        pickupTitle,
        isDeliveryPossible,
        isPickupPossible,
    ]);

    if (!isDeliveryAvailable && !isPickupAvailable) {
        return null;
    }

    return (
        <FormProvider {...formMethods}>
            <Layout
                className="select-delivery-box"
                data-testid={props['data-testid'] ?? 'select-delivery-box'}
                data-delivery-type={deliveryType}
                {...props}
            >
                <div className="select-delivery-box__wrapper">
                    <div className="select-delivery-box__tiles">
                        {tiles.map((tile) => (
                            <SelectDeliveryBoxDeliveryOption
                                key={tile.type}
                                value={tile.type}
                                title={tile.title}
                                icon={
                                    isIconVisible && tile.icon
                                        ? (svgProps) => <Icon name={tile.icon!} {...svgProps} />
                                        : null
                                }
                                time={tile.time}
                                isActive={tile.type === deliveryType}
                                onClick={() => handleChangeDeliveryType(tile.type)}
                                data-testid={tile.testId}
                            />
                        ))}
                    </div>

                    {deliveryType === DeliveryType.DELIVERY && (
                        <FormAddressAutocompleteField name="address" onChange={handleChangeAddress} />
                    )}

                    {selectedVenue && (
                        <div
                            className="select-delivery-box__redirect"
                            data-testid="select-delivery-box-redirect"
                        >
                            <div className="select-delivery-box__redirect-spinner" aria-hidden="true" />
                            <p className="select-delivery-box__redirect-text">
                                {t('SELECT_DELIVERY_BOX.REDIRECTING', { venue: selectedVenue.name })}
                            </p>
                        </div>
                    )}
                </div>
                {((deliveryType === DeliveryType.DELIVERY && !!address) ||
                    deliveryType === DeliveryType.PICKUP) && (
                    <>
                        {!!filteredVenues?.length && (
                            <SelectDeliveryBoxVenueList
                                heading={
                                    deliveryType === DeliveryType.DELIVERY
                                        ? deliveryHeading
                                            ? getLocalizedValue(deliveryHeading)
                                            : t('SELECT_DELIVERY_BOX.DELIVERY_HEADING')
                                        : pickupHeading
                                          ? getLocalizedValue(pickupHeading)
                                          : t('SELECT_DELIVERY_BOX.PICKUP_HEADING')
                                }
                                venues={filteredVenues}
                                venueCard={venueCard}
                                deliveryType={deliveryType}
                                address={address}
                            />
                        )}
                        {!selectedVenue && !!address && !address.street && (
                            <p
                                className="select-delivery-box__select-from-suggestions-text"
                                data-testid="select-delivery-box-select-address-from-suggestions-text"
                            >
                                {t('SELECT_DELIVERY_BOX.SELECT_ADDRESS_FROM_SUGGESTIONS')}
                            </p>
                        )}
                        {!selectedVenue && !filteredVenues.length && !!address?.street && (
                            <p
                                className="select-delivery-box__no-match"
                                data-testid="select-delivery-box-no-match"
                            >
                                {t('SELECT_DELIVERY_BOX.NO_MATCH')}
                            </p>
                        )}
                    </>
                )}
            </Layout>
        </FormProvider>
    );
};
